import React, { useMemo, useState } from 'react';
import { Box, Avatar, Typography } from '@mui/material';
import { keyframes } from '@emotion/react';
import styled from '@emotion/styled';
import { Player } from '../types';
import CartoonButton from './CartoonButton.component';

const ROAST_LINES = [
  "Time to open your wallet 👛",
  "Your bank account called. It's crying. 😭",
  "The wheel has spoken. No refunds. 🎡",
  "Congratulations! You're buying everyone a round 🍺",
  "Should've stayed home today 💸",
  "The universe has decided: you're broke now 🌍",
  "Not lucky at cards, not lucky at wheels 🃏",
  "Your friends thank you in advance 🙏",
  "Skill issue. Pay up. 💀",
  "This is what you get for existing 🫵",
  "The wheel never lies. Unlike you. 😅",
  "Don't worry, money can't buy happiness anyway 🤷",
];

interface WinnerAnnouncementProps {
  winner: Player;
  isAdmin: boolean;
  onSpinAgain?: () => void;
  onGoHome?: () => void;
}

const popIn = keyframes`
  0% { transform: scale(0) rotate(-10deg); opacity: 0; }
  60% { transform: scale(1.15) rotate(3deg); opacity: 1; }
  80% { transform: scale(0.95) rotate(-1deg); }
  100% { transform: scale(1) rotate(0deg); opacity: 1; }
`;

const AnimatedCard = styled(Box)`
  animation: ${popIn} 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
  background: white;
  border: 5px solid black;
  border-radius: 24px;
  box-shadow: 0 8px 0 black, 0 12px 30px rgba(0, 0, 0, 0.4);
  padding: 40px 48px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  max-width: 360px;
  width: 90%;
`;

const WinnerAnnouncement: React.FC<WinnerAnnouncementProps> = ({ winner, isAdmin, onSpinAgain, onGoHome }) => {
  const roastLine = useMemo(() => ROAST_LINES[Math.floor(Math.random() * ROAST_LINES.length)], []);
  const [confirmingLeave, setConfirmingLeave] = useState(false);

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(0, 0, 0, 0.65)',
      }}
    >
      <AnimatedCard>
        {/* Avatar */}
        {winner.avatarType === 'custom' ? (
          <Avatar
            src={winner.avatar}
            sx={{ width: 120, height: 120, border: `4px solid ${winner.color}` }}
          />
        ) : (
          <Box
            sx={{
              width: 120,
              height: 120,
              borderRadius: '50%',
              border: `4px solid ${winner.color}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '5rem',
              background: 'rgba(0,0,0,0.05)',
            }}
          >
            {winner.avatar}
          </Box>
        )}

        {/* Winner text */}
        <Typography
          variant="h4"
          sx={{
            fontFamily: '"Comic Sans MS", cursive, sans-serif',
            fontWeight: 'bold',
            color: winner.color,
            textAlign: 'center',
            textShadow: '2px 2px 0 rgba(0,0,0,0.15)',
          }}
        >
          🎉 {winner.name} pays! 🎉
        </Typography>

        {/* Roast line */}
        <Typography
          variant="body1"
          sx={{
            fontFamily: '"Comic Sans MS", cursive, sans-serif',
            color: '#555',
            textAlign: 'center',
          }}
        >
          {roastLine}
        </Typography>

        {isAdmin && onSpinAgain && (
          <CartoonButton
            variant="contained"
            onClick={onSpinAgain}
            sx={{ mt: 1, background: '#ff4081' }}
          >
            Spin Again!
          </CartoonButton>
        )}

        {/* Back to Main Menu — confirm before leaving */}
        {!confirmingLeave ? (
          <CartoonButton
            variant="contained"
            onClick={() => setConfirmingLeave(true)}
            sx={{ background: '#555', color: 'white' }}
          >
            Back to Main Menu
          </CartoonButton>
        ) : (
          <Box display="flex" flexDirection="column" alignItems="center" gap="8px" width="100%">
            <Typography
              variant="body2"
              sx={{ fontFamily: '"Comic Sans MS", cursive, sans-serif', color: '#333', textAlign: 'center' }}
            >
              Leave this lobby?
            </Typography>
            <Box display="flex" gap="8px">
              <CartoonButton
                variant="contained"
                onClick={() => setConfirmingLeave(false)}
                sx={{ background: '#888' }}
              >
                Cancel
              </CartoonButton>
              <CartoonButton
                variant="contained"
                onClick={onGoHome}
                sx={{ background: '#d32f2f' }}
              >
                Leave
              </CartoonButton>
            </Box>
          </Box>
        )}
      </AnimatedCard>
    </Box>
  );
};

export default WinnerAnnouncement;
